import React from 'react';

import { EditIcon } from '@vlabs/icons';
import { useModal } from '@vlabs/uikit';
import type { ButtonProps } from '@vlabs/uikit/components/controls/button/Button';
import { Button } from '@vlabs/uikit/components/controls/button/Button';
import { toast } from 'react-toastify';

import { RoleForm } from '@vlabs/monokit/entities/roles';
import { useGetRole, usePatchRole } from '@vlabs/api/artemy/client';
import type { PatchRoleRequest } from '@vlabs/api/artemy/model';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { useEditRoleTranslation } from './i18n';
import type { EditRoleProps } from './types';

export type UpdateDefaultValues = Omit<PatchRoleRequest, 'permissions'>;

interface EditRoleButtonProps extends Omit<ButtonProps, 'onClick'>, EditRoleProps {
  onEditRole?: VoidFunction;
}

const displayName = 'EditRoleButton';
const testid = buildTestid(displayName);

function EditRoleButton({
  roleId,
  'data-testid': dataTestId = undefined,
  onEditRole = undefined,
  ...props
}: EditRoleButtonProps): React.JSX.Element {
  const { t } = useEditRoleTranslation();
  const modal = useModal({ size: 'small' });
  const getRole = useGetRole(roleId);
  const patchRole = usePatchRole({
    mutation: {
      onSuccess: () => {
        modal.close();
        toast.success(t('toast_success'));
        onEditRole?.();
      },
      onError: () => {
        toast.error(t('toast_error'));
      },
    },
  });

  const defaultValues: UpdateDefaultValues = {
    name: getRole.data?.name,
    description: getRole.data?.description,
  };

  const handleSubmit = (values: UpdateDefaultValues) => {
    patchRole.mutate({ roleId, data: values });
  };

  return (
    <React.Fragment>
      {modal.wrap(
        <RoleForm
          context="update"
          defaultValues={defaultValues}
          onCancel={modal.close}
          onSubmit={handleSubmit}
        />,
      )}

      <Button
        icon={<EditIcon />}
        onClick={modal.open}
        variant="flat"
        {...testid(dataTestId)}
        {...props}
      >
        {props.children ?? t('edit_button')}
      </Button>
    </React.Fragment>
  );
}

EditRoleButton.displayName = displayName;

export { EditRoleButton };
